import React from 'react';
import { useParams } from 'react-router-dom';
import { TriangleIcon, CheckIcon } from 'lucide-react';
import { CRESTMONT, CrestmontHeader, CrestmontFooter } from '../components/crestmont/CrestmontChrome';
import { CrestmontLeadForm } from '../components/crestmont/CrestmontLeadForm';
import { CurriculumSection, SpecializationsSection } from '../components/crestmont/CrestmontCurriculum';
import { BenefitsSection, CtaBand, DifferentiatorsSection, FaqSection, RequirementsSection } from '../components/crestmont/CrestmontDetails';
import { crestmontPrograms, getCrestmontProgram } from '../data/crestmontPrograms';
import { useMetaTags } from '../hooks/useMetaTags';

// Degree-specific Crestmont University landing page, built to the Crestmont
// brand guide. Routed at /universities/crestmont-university/<program-slug>;
// `programSlug` is also read from the path so an explicitly registered route
// (which carries no param) still resolves.
interface CrestmontProgramPageProps {
  /** Explicit slug when the route is registered without a param. */
  slug?: string;
}
export function CrestmontProgramPage({
  slug
}: CrestmontProgramPageProps) {
  const params = useParams<{
    programSlug?: string;
  }>();
  const resolvedSlug = slug || params.programSlug || window.location.pathname.split('/').filter(Boolean).pop();
  const program = getCrestmontProgram(resolvedSlug);
  useMetaTags({
    title: program ? `${program.name} Online | Crestmont University` : 'Crestmont University',
    description: program?.heroSubhead || 'Online degree programs from Crestmont University.',
    robots: 'noindex, follow'
  });
  const scrollToForm = () => {
    const target = document.getElementById('apply');
    if (target) target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  if (!program) {
    return <div className="min-h-screen bg-white flex flex-col" data-id="element-1444" data-mp="59ftoogsdjtc">
        <CrestmontHeader onApply={scrollToForm} data-id="element-1445" data-mp="9dno929xdtzh" />
        <main className="flex-1 max-w-3xl mx-auto px-4 py-24 text-center" data-id="element-1446" data-mp="26di42iwqcy5x">
          <h1 className="text-2xl font-extrabold" style={{
          color: CRESTMONT.navy
        }} data-id="element-1447" data-mp="26be0k22qu166">
            This program isn’t available
          </h1>
          <p className="mt-3 text-sm text-gray-600" data-id="element-1448" data-mp="2f7d81nm48ijz">
            Browse the full list of Crestmont degrees to find the right fit.
          </p>
          <a href="/universities/crestmont-university" className="mt-6 inline-block px-6 py-3 rounded text-sm font-bold" style={{
          backgroundColor: CRESTMONT.gold,
          color: CRESTMONT.ink
        }} data-id="element-1449" data-mp="2p3zps3j5aths">
            View all programs
          </a>
        </main>
        <CrestmontFooter data-id="element-1450" data-mp="2y2boksu60xlb" />
      </div>;
  }
  const programOptions = Object.values(crestmontPrograms).map(entry => ({
    label: entry.name,
    value: entry.mProgramValue
  }));
  return <div className="min-h-screen bg-white w-full" data-id="element-1451" data-mp="13sgf42mt997c">
      <CrestmontHeader onApply={scrollToForm} data-id="element-1452" data-mp="zq39rfbz7db3" />

      <main data-id="element-1453" data-mp="zrecdbtoqf0m">
        {/* Hero — the request form lives here so it is reachable without a
            scroll on phones. On mobile a compressed headline sits above the
            form; the full pitch follows underneath it. */}
        <section className="py-8 md:py-16 lg:py-20" style={{
        backgroundColor: CRESTMONT.navy
      }} data-id="element-1454" data-mp="fvtns0h4i9af">
          <div className="max-w-6xl mx-auto px-4 grid gap-8 lg:grid-cols-5 lg:gap-12 lg:items-start" data-id="element-1455" data-mp="kt3jfxv3vh6p">
            <div className="lg:col-span-3" data-id="element-1456" data-mp="5wceza6krfa8">
              <TriangleIcon className="w-4 h-4 md:w-5 md:h-5 mb-3 md:mb-5" style={{
              color: CRESTMONT.gold
            }} fill={CRESTMONT.gold} aria-hidden="true" data-id="element-1457" data-mp="aw7ajr1aot1m" />
              <p className="lg:hidden text-[11px] font-bold uppercase tracking-[0.16em] text-[#fcc23f]" data-id="element-1458" data-mp="1ur05jahcqxe">
                {program.degreeLevel} · 100% Online
              </p>
              <h1 className="mt-1.5 lg:mt-0 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] lg:leading-[1.05] tracking-tight max-w-2xl" data-id="element-1459" data-mp="1w9a1hijacodu">
                {program.name}
                <span className="block text-white/60" data-id="element-1460" data-mp="3ndv66vjz9oug">({program.code})</span>
              </h1>
              <p className="mt-3 lg:mt-5 text-sm md:text-base text-white/75 max-w-xl leading-relaxed" data-id="element-1461" data-mp="3ie8s6jwbs4kl">
                {program.heroSubhead}
              </p>
              {/* Desktop-only supporting proof — on mobile the form takes this
                  space and the same content appears in the section below. */}
              <ul className="hidden lg:block mt-8 space-y-2.5 max-w-lg" data-id="element-1462" data-mp="34mu5a1zarj89">
                {program.positioningBullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1463" data-mp="2zoaki4wfwdhh">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.gold
                }} aria-hidden="true" data-id="element-1464" data-mp="2upzclzppgd1w" />
                    <span className="text-sm leading-relaxed text-white/80" data-id="element-1465" data-mp="2loroubkteum2">
                      {bullet}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="lg:col-span-2" data-id="element-1466" data-mp="163h17zyjmox8">
              <CrestmontLeadForm programValue={program.mProgramValue} programName={program.name} programOptions={programOptions} data-id="element-1467" data-mp="1a6dnq1r7f56g" />
            </div>
          </div>
        </section>

        {/* Positioning */}
        <section className="py-12 md:py-20 bg-white" data-id="element-1468" data-mp="idwer736l9j7">
          <div className="max-w-6xl mx-auto px-4 grid gap-8 lg:grid-cols-5 lg:gap-12 lg:items-start" data-id="element-1469" data-mp="michizhi00u7">
            <div className="lg:col-span-3" data-id="element-1470" data-mp="rfm6yf4w0ci1">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#00426b]/60" data-id="element-1471" data-mp="rekxgf3iry2f">
                {program.positioningEyebrow}
              </p>
              <h2 className="mt-2 text-2xl md:text-4xl font-extrabold tracking-tight leading-tight max-w-lg" style={{
              color: CRESTMONT.navy
            }} data-id="element-1472" data-mp="3uwl6lxkta9hm">
                {program.positioningHeading}
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-gray-600 max-w-lg" data-id="element-1473" data-mp="122vzz5wwjzub">
                {program.positioningBody}
              </p>
              {/* Mobile repeat of the hero proof points */}
              <ul className="lg:hidden mt-6 space-y-2.5" data-id="element-1474" data-mp="1bs774n08aufk">
                {program.positioningBullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5" data-id="element-1475" data-mp="o3o16e4nitqn">
                    <CheckIcon className="mt-0.5 w-4 h-4 flex-shrink-0" style={{
                  color: CRESTMONT.navy
                }} aria-hidden="true" data-id="element-1476" data-mp="j4jsrppjx01d" />
                    <span className="text-sm leading-relaxed text-gray-700" data-id="element-1477" data-mp="s3evusxatu0g">
                      {bullet}
                    </span>
                  </li>)}
              </ul>
            </div>

            <dl className="lg:col-span-2 grid grid-cols-3 lg:grid-cols-1 gap-3" data-id="element-1478" data-mp="23itx48wjj81d">
              {program.stats.map(stat => <div key={stat.label} className="rounded-md px-4 py-4 text-center lg:text-left" style={{
              backgroundColor: CRESTMONT.mist
            }} data-id="element-1479" data-mp="2c2qcgkbo8e4m">
                  <dt className="sr-only" data-id="element-1480" data-mp="271j86v8qlx2p">{stat.label}</dt>
                  <dd data-id="element-1481" data-mp="2722pqauh6jg7">
                    <span className="block text-lg sm:text-xl md:text-2xl font-extrabold tabular-nums" style={{
                  color: CRESTMONT.navy
                }} data-id="element-1482" data-mp="273nax9uwgq1e">
                      {stat.value}
                    </span>
                    <span className="mt-1 block text-[10px] md:text-xs leading-snug text-gray-500" data-id="element-1483" data-mp="2l0iuvxcow1w8">
                      {stat.label}
                    </span>
                  </dd>
                </div>)}
            </dl>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 md:py-20" style={{
        backgroundColor: CRESTMONT.ink
      }} aria-labelledby="testimonial-heading" data-id="element-1484" data-mp="3ihhn3fqljdbu">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1485" data-mp="1dnzy8gjkax8c">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#fcc23f]" data-id="element-1486" data-mp="1dmf60xkpzqdn">
              {program.testimonial.eyebrow}
            </p>
            <h2 id="testimonial-heading" className="mt-2 text-2xl md:text-3xl font-extrabold text-white tracking-tight max-w-xl" data-id="element-1487" data-mp="14nlc5eyrxe3s">
              {program.testimonial.heading}
            </h2>
            <p className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed" data-id="element-1488" data-mp="1wfj02k1uo58s">
              {program.testimonial.subheading}
            </p>

            <figure className="mt-10 grid gap-8 md:grid-cols-5 md:items-center" data-id="element-1489" data-mp="1ndjjttynt56w">
              <div className="md:col-span-2 flex justify-center" data-id="element-1490" data-mp="1nfnmk8ktny5l">
                <img src={program.testimonial.imageUrl} alt={program.testimonial.name} className="w-40 h-40 md:w-48 md:h-48 rounded-full object-cover object-top border-4 border-white/10" data-id="element-1491" data-mp="gy8udqpdrxpv" />
              </div>
              <blockquote className="md:col-span-3" data-id="element-1492" data-mp="2sk1cdbjp20us">
                <p className="text-base md:text-lg leading-relaxed text-white/85" data-id="element-1493" data-mp="2siq3wyfb04xc">
                  “{program.testimonial.quote}”
                </p>
                <figcaption className="mt-5" data-id="element-1494" data-mp="31e68a9doq2h6">
                  <span className="block text-sm font-bold text-[#fcc23f]" data-id="element-1495" data-mp="31f8uw53z5ira">
                    {program.testimonial.name}
                  </span>
                  <span className="block text-xs text-white/60" data-id="element-1496" data-mp="3baixov275u64">
                    {program.testimonial.title}
                  </span>
                  <span className="block text-xs text-white/40" data-id="element-1497" data-mp="3gb6n5cas22y8">
                    {program.testimonial.detail}
                  </span>
                </figcaption>
              </blockquote>
            </figure>
          </div>
        </section>

        <CurriculumSection program={program} data-id="element-1498" data-mp="1pxfykga1ysit" />
        <SpecializationsSection program={program} data-id="element-1499" data-mp="1py98fq4b5y56" />
        <CtaBand heading={program.ctaBand.heading} body={program.ctaBand.body} onApply={scrollToForm} data-id="element-1500" data-mp="3cqvwpnohg921" />
        <DifferentiatorsSection program={program} data-id="element-1501" data-mp="3gshffpnp7hk5" />
        <RequirementsSection program={program} onApply={scrollToForm} data-id="element-1502" data-mp="3lqj18iciduzb" />
        <BenefitsSection benefits={program.benefits} data-id="element-1503" data-mp="3ltexm28pkbln" />
        <FaqSection faq={program.faq} data-id="element-1504" data-mp="3qqyc939biqe4" />
      </main>

      <CrestmontFooter data-id="element-1505" data-mp="3vp86nbn7xmyo" />
    </div>;
}